import { useState, useEffect } from 'react';
import { Flame, Target } from 'lucide-react';
import { CalculatorCard, SliderInput } from '../CalculatorCard';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

export const FireCalculator = () => {
  const [monthlyExpenses, setMonthlyExpenses] = useState(40000);
  const [currentSavings, setCurrentSavings] = useState(500000);
  const [monthlyInvestment, setMonthlyInvestment] = useState(15000);
  const [expectedReturn, setExpectedReturn] = useState(7);
  
  const [fireNumber, setFireNumber] = useState(0);
  const [yearsToFire, setYearsToFire] = useState(0);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    const withdrawalRate = 0.04;
    const annualExpenses = monthlyExpenses * 12;
    const fireTarget = annualExpenses / withdrawalRate;
    setFireNumber(Math.round(fireTarget));

    const monthlyRate = expectedReturn / 100 / 12;
    let balance = currentSavings;
    let months = 0;
    const maxMonths = 50 * 12;
    
    const data = [{ year: 0, portfolio: currentSavings }];

    while (balance < fireTarget && months < maxMonths) {
      balance = balance * (1 + monthlyRate) + monthlyInvestment;
      months++;
      
      if (months % 12 === 0) {
        data.push({ year: months / 12, portfolio: Math.round(balance) });
      }
    }

    setYearsToFire(months < maxMonths ? Math.round(months / 12 * 10) / 10 : -1);
    setChartData(data);
  }, [monthlyExpenses, currentSavings, monthlyInvestment, expectedReturn]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-navy border border-white/10 rounded-lg p-3 shadow-lg">
          <p className="text-white font-semibold">{label}. rok</p>
          <p className="text-green font-bold">{payload[0]?.value?.toLocaleString('cs-CZ')} Kč</p>
        </div>
      );
    }
    return null;
  };

  const monthlyPassiveIncome = Math.round((fireNumber * 0.04) / 12);

  const result = (
    <div className="space-y-6">
      <div className="text-right">
        <p className="text-gray-400 text-sm">FIRE cíl (finanční nezávislost)</p>
        <p className="text-4xl font-bold text-gold">{fireNumber.toLocaleString('cs-CZ')} Kč</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Cesta k FIRE</p>
          <p className="text-orange-400 font-bold text-lg">
            {yearsToFire > 0 ? `${yearsToFire} let` : '50+ let'}
          </p>
        </div>
        <div className="bg-white/5 rounded-xl p-4">
          <p className="text-gray-400 text-xs">Pasivní příjem</p>
          <p className="text-green font-bold text-lg">{monthlyPassiveIncome.toLocaleString('cs-CZ')} Kč/měs</p>
        </div>
      </div>

      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorFire" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#F97316" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#F97316" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="year" 
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
            />
            <YAxis 
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
              tickFormatter={(value) => `${(value / 1000000).toFixed(0)}M`}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine 
              y={fireNumber} 
              stroke="#22C55E" 
              strokeDasharray="5 5"
            />
            <Area 
              type="monotone" 
              dataKey="portfolio" 
              stroke="#F97316" 
              fill="url(#colorFire)" 
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {yearsToFire > 0 && yearsToFire <= 30 && (
        <div className="bg-green/10 rounded-xl p-4">
          <p className="text-green text-sm">
            Za <span className="font-bold">{yearsToFire} let</span> můžete přestat pracovat a žít z pasivního příjmu {monthlyExpenses.toLocaleString('cs-CZ')} Kč měsíčně.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <CalculatorCard
      id="fire"
      title="FIRE kalkulačka"
      subtitle="Kdy dosáhnete finanční nezávislosti?"
      icon={Flame}
      tip="Pravidlo 4% říká, že z diverzifikovaného portfolia můžete ročně vybírat 4% a statisticky vydrží navždy. Snížení výdajů má dvojitý efekt: méně potřebujete a víc můžete investovat."
      result={result}
      showResult={true}
      testId="fire-calculator"
    >
      <SliderInput
        label="Měsíční výdaje"
        value={monthlyExpenses}
        onChange={setMonthlyExpenses}
        min={15000}
        max={150000}
        step={1000}
        suffix="Kč"
        testId="fire-expenses-slider"
      />
      <SliderInput
        label="Současné investice"
        value={currentSavings}
        onChange={setCurrentSavings}
        min={0}
        max={10000000}
        step={50000}
        suffix="Kč"
        testId="fire-savings-slider"
      />
      <SliderInput
        label="Měsíční investice"
        value={monthlyInvestment}
        onChange={setMonthlyInvestment}
        min={1000}
        max={100000}
        step={1000}
        suffix="Kč"
        testId="fire-investment-slider"
      />
      <SliderInput
        label="Očekávaný roční výnos"
        value={expectedReturn}
        onChange={setExpectedReturn}
        min={3}
        max={12}
        step={0.5}
        suffix="%"
        formatValue={(v) => `${v.toFixed(1)} %`}
        testId="fire-return-slider"
      />
    </CalculatorCard>
  );
};
